import React from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from './InspectionRadioGroup';
import { conditionOptions, conditionWithNaOptions, yesNoOptions, hvacAgeOptions } from '../config/options';

export interface HvacSystemValues {
  location: string;
  manufacturer: string;
  age: string;
  ageCondition: string;
  systemType: string;
  overallVisualCondition: string;
  overallOperation: string;
  properClearance: string;
  serviceRecordUpdated: string;
  notes: string;
  // Boiler-specific fields (only used when systemType is 'boiler')
  boilerAcPresent: string;
  boilerExpansionTankCondition: string;
  boilerHeatExchangerCondition: string;
  boilerPressureReliefCondition: string;
  boilerAutoFillCondition: string;
}

export const defaultHvacSystem: HvacSystemValues = {
  location: '',
  manufacturer: '',
  age: '',
  ageCondition: '',
  systemType: '',
  overallVisualCondition: '',
  overallOperation: '',
  properClearance: '',
  serviceRecordUpdated: '',
  notes: '',
  boilerAcPresent: '',
  boilerExpansionTankCondition: '',
  boilerHeatExchangerCondition: '',
  boilerPressureReliefCondition: '',
  boilerAutoFillCondition: '',
};

interface ManufacturerOption {
  value: string;
  label: string;
}

interface HvacSystemFieldsProps {
  values: HvacSystemValues;
  onChange: (field: keyof HvacSystemValues, value: any) => void;
  manufacturers: ManufacturerOption[];
}

const subHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1.5,
};

const getAgeColor = (value: string): string | undefined => {
  if (!value || value === 'n_a') return undefined;
  const map: Record<string, string> = { '1_10': '#4caf50', '11_15': '#ff9800', '15_plus': '#f44336' };
  return map[value];
};

const getAgeCondition = (value: string): string => {
  if (!value || value === 'n_a') return '';
  const map: Record<string, string> = { '1_10': 'good', '11_15': 'needs_attention', '15_plus': 'requires_service' };
  return map[value] || '';
};

const HvacSystemFields: React.FC<HvacSystemFieldsProps> = ({ values, onChange, manufacturers }) => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="filled">
          <InputLabel>Manufacturer</InputLabel>
          <Select value={values.manufacturer} onChange={(e) => onChange('manufacturer', e.target.value)}>
            {manufacturers.map((opt) => (
              <MenuItem key={opt.value} value={opt.label}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="filled">
          <InputLabel>Age</InputLabel>
          <Select
            value={values.age}
            onChange={(e) => {
              const val = e.target.value as string;
              onChange('age', val);
              onChange('ageCondition', getAgeCondition(val));
              (e.target as HTMLInputElement).blur();
            }}
            sx={{
              ...(getAgeColor(values.age) && {
                bgcolor: getAgeColor(values.age),
                color: '#fff',
                '&.Mui-focused': { bgcolor: getAgeColor(values.age) },
                '& .MuiSelect-icon': { color: '#fff' },
                '& .MuiFilledInput-input': { color: '#fff' },
              }),
            }}
          >
            {hvacAgeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput label="Location" value={values.location} valueChanged={(val) => onChange('location', val)} />
      </Grid>
    </Grid>

    <Typography sx={subHeaderSx}>Type</Typography>
    <Box sx={{ mb: 2 }}>
      <InspectionRadioGroup
        radioOptions={[
          { label: 'Split System', value: 'split_system' },
          { label: 'Package Unit', value: 'package_unit' },
          { label: 'Heat Pump', value: 'heat_pump' },
          { label: 'Mini Split', value: 'mini_split' },
          { label: 'Geothermal', value: 'geothermal' },
          { label: 'Boiler', value: 'boiler' },
        ]}
        value={values.systemType}
        setValue={(val) => {
          onChange('systemType', val);
          if (val !== 'boiler') {
            onChange('boilerAcPresent', '');
            onChange('boilerExpansionTankCondition', '');
            onChange('boilerHeatExchangerCondition', '');
            onChange('boilerPressureReliefCondition', '');
            onChange('boilerAutoFillCondition', '');
          }
        }}
      />
    </Box>

    <Typography sx={subHeaderSx}>Inspection Details</Typography>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {values.systemType === 'boiler' && (
        <>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Air Conditioning"
              radioOptions={yesNoOptions}
              value={values.boilerAcPresent}
              setValue={(val) => onChange('boilerAcPresent', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Expansion Tank"
              radioOptions={conditionWithNaOptions}
              value={values.boilerExpansionTankCondition}
              setValue={(val) => onChange('boilerExpansionTankCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Heat Exchanger"
              radioOptions={conditionWithNaOptions}
              value={values.boilerHeatExchangerCondition}
              setValue={(val) => onChange('boilerHeatExchangerCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Pressure Relief"
              radioOptions={conditionWithNaOptions}
              value={values.boilerPressureReliefCondition}
              setValue={(val) => onChange('boilerPressureReliefCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Auto-Fill"
              radioOptions={conditionWithNaOptions}
              value={values.boilerAutoFillCondition}
              setValue={(val) => onChange('boilerAutoFillCondition', val)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Overall/Visual Condition"
          radioOptions={conditionOptions}
          value={values.overallVisualCondition}
          setValue={(val) => onChange('overallVisualCondition', val)}
        />
      </Grid>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Overall Operation"
          radioOptions={conditionOptions}
          value={values.overallOperation}
          setValue={(val) => onChange('overallOperation', val)}
        />
      </Grid>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Proper Clearance Around Equipment"
          radioOptions={yesNoOptions}
          value={values.properClearance}
          setValue={(val) => onChange('properClearance', val)}
        />
      </Grid>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Service Record Updated"
          radioOptions={yesNoOptions}
          value={values.serviceRecordUpdated}
          setValue={(val) => onChange('serviceRecordUpdated', val)}
        />
      </Grid>
    </Grid>

    <TextInput
      label="HVAC Notes"
      value={values.notes}
      valueChanged={(val) => onChange('notes', val)}
      multiline
      rows={3}
    />
  </Box>
);

export default HvacSystemFields;
